import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Grid,
  GridItem,
  Icon,
  Link,
  Flex,
  SimpleGrid,
  Image
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { 
  FaGem,
  FaHeart,
  FaShieldAlt,
  FaArrowRight,
  FaEnvelope,
  FaQuestionCircle,
  FaShoppingBag,
  FaCrown,
  FaHandshake,
  FaAward
} from 'react-icons/fa';
import { 
  HiSparkles,
  HiShieldCheck,
  HiHeart
} from 'react-icons/hi2';
import { Link as RouterLink } from 'react-router-dom';
import SEO from '../components/SEO';

// Add Google Fonts for luxury typography
const fontImport = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700&family=Inter:wght@300;400;500;600&display=swap');
`;

// Inject the font styles
if (typeof document !== 'undefined' && !document.head.querySelector('[data-about-fonts]')) {
  const styleElement = document.createElement('style');
  styleElement.textContent = fontImport;
  styleElement.setAttribute('data-about-fonts', 'true');
  document.head.appendChild(styleElement);
}

const MotionBox = motion(Box);
const MotionGrid = motion(Grid);

const AboutUsPage = () => {
  const coreValues = [
    {
      icon: FaCrown,
      title: 'Premium Quality',
      description: 'We use only premium-grade stainless steel, ensuring every piece is built to last and feels luxurious.'
    },
    {
      icon: FaGem,
      title: 'Timeless Design',
      description: 'Our designs are clean, bold, and timeless — created to make a statement that transcends trends.'
    },
    {
      icon: FaHeart,
      title: 'Crafted with Passion',
      description: 'Every detail is handled with care, passion, and precision from the initial design to final delivery.'
    },
    {
      icon: FaHandshake,
      title: 'Personal Experience',
      description: 'We believe in a brand experience that feels personal, building lasting relationships with our customers.'
    },
    {
      icon: FaShieldAlt,
      title: 'Built to Last',
      description: 'Quality that endures — our jewelry is designed for daily wear with anti-tarnish and water-resistant properties.'
    },
    {
      icon: FaAward,
      title: 'Statement Pieces',
      description: 'This isn\'t just jewelry. Every piece is crafted to be a statement of presence and confidence.'
    }
  ];

  const ctaOptions = [
    {
      icon: FaShoppingBag,
      title: 'Explore',
      description: 'Discover our range of premium jewelry',
      href: '/products/rings',
      color: 'gray.800',
      bg: 'white'
    },
    {
      icon: FaQuestionCircle,
      title: 'Have Questions?',
      description: 'Find answers in our comprehensive FAQ',
      href: '/faqs',
      color: 'white',
      bg: 'linear-gradient(135deg, #2d3748 0%, #4a5568 100%)'
    },
    {
      icon: FaEnvelope,
      title: 'Get in Touch',
      description: 'Contact our team for personalized assistance',
      href: '/contact',
      color: 'gray.800',
      bg: 'linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%)'
    }
  ];

  return (
    <>
      <SEO 
        title="About Us - Our Story & Vision"
        description="Learn about AGNIR's journey in crafting premium handmade jewelry. We blend fire-inspired aesthetics with timeless elegance, creating exquisite rings, bracelets, chains, and pendants with passion and precision."
        keywords="about AGNIR, AGNIR story, jewelry brand, handcrafted jewelry, premium jewelry company, fire-inspired jewelry, luxury accessories brand, jewelry craftsmanship"
        url="https://agnir.co/about-us"
        image="https://agnir.co/hero-bg.webp"
      />
      <Box 
        minH="100vh" 
        bgImage={`url(${process.env.REACT_APP_BG_URL})`}
        py={{ base: 10, lg: 20 }}
      >
      <Container maxW="container.xl">
        {/* Hero Section */}
        <MotionBox
          textAlign="center"
          mb={{ base: 12, md: 16, lg: 20 }}
          px={{ base: 4, md: 0 }}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <VStack spacing={{ base: 6, lg: 8 }}>
            <HStack justify="center" mb={4}>
              <Icon
                as={FaGem} 
                boxSize={{ base: 10, lg: 12 }} 
                color="gray.600" 
              />
            </HStack>
            
            <Heading
              as="h1"
              fontSize={{ base: "4xl", sm: "5xl", md: "6xl", lg: "7xl" }}
              fontFamily="'Cormorant', serif"
              fontWeight="700"
              color="gray.800"
              lineHeight={{ base: "1.2", md: "1.1" }}
              maxW="800px"
              px={{ base: 4, md: 0 }}
            >
              For the ones who wear{' '}
              <Text as="span" 
                bgGradient="linear(to-r, gray.700, gray.900)"
                bgClip="text"
              >
                presence
              </Text>
            </Heading>

            <Box maxW="600px" px={{ base: 4, md: 0 }}>
              <Text
                fontSize={{ base: "xl", md: "2xl", lg: "3xl" }}
                color="gray.600"
                fontFamily="'Zain', sans-serif"
                lineHeight="1.7"
                fontWeight="500"
                mb={{ base: 4, md: 6 }}
              >
                At AGNIR, we don't just make jewelry—we create presence.
              </Text>
              <Text
                fontSize={{ base: "md", md: "lg", lg: "xl" }}
                color="gray.600"
                fontFamily="'Zain', sans-serif"
                lineHeight="1.6"
                fontWeight="500"
              >
                Every piece is a statement: clean, bold, and timeless.
              </Text>
            </Box>

            {/* Hero Visual Elements */}
            <MotionGrid
              templateColumns={{ base: 'repeat(3, 1fr)', md: 'repeat(3, 1fr)' }}
              gap={{ base: 4, md: 6 }}
              maxW={{ base: "100%", md: "600px" }}
              w="full"
              mt={8}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              {[
                { icon: HiSparkles, text: 'Premium Grade' },
                { icon: HiShieldCheck, text: 'Built to Last' },
                { icon: HiHeart, text: 'Crafted with Care' }
              ].map((item, index) => (
                <VStack key={item.text} spacing={{ base: 2, md: 3 }}>
                  <Box
                    p={{ base: 3, md: 4 }}
                    borderRadius="full"
                    bg="white"
                    boxShadow="0 4px 15px rgba(0, 0, 0, 0.1)"
                    border="1px solid"
                    borderColor="gray.200"
                  >
                    <Icon as={item.icon} boxSize={{ base: 5, md: 6 }} color="gray.600" />
                  </Box>
                  <Text
                    fontSize={{ base: "xs", md: "sm" }}
                    fontFamily="'Zain', sans-serif"
                    color="gray.600"
                    fontWeight="500"
                    textAlign="center"
                    lineHeight="1.3"
                  >
                    {item.text}
                  </Text>
                </VStack>
              ))}
            </MotionGrid>
          </VStack>
        </MotionBox>

        {/* Brand Story Section */}
        <MotionBox
          mb={{ base: 12, md: 16, lg: 20 }}
          px={{ base: 4, md: 0 }}
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <Box
            bg="white"
            borderRadius="3xl"
            p={{ base: 8, lg: 16 }}
            boxShadow="0 20px 60px rgba(0, 0, 0, 0.1)"
            border="1px solid"
            borderColor="gray.100"
            position="relative"
            overflow="hidden"
          >
            {/* Background Pattern */}
            <Box
              position="absolute"
              top="0"
              right="0"
              w="200px"
              h="200px"
              opacity="0.05"
              backgroundImage="radial-gradient(circle, gray.400 1px, transparent 1px)"
              backgroundSize="20px 20px"
            />
            
            <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={{ base: 8, lg: 16 }} alignItems="center">
              <GridItem>
                <VStack spacing={6} align="start">
                  <Heading
                    as="h2"
                    size={{ base: "2xl", lg: "3xl" }}
                    fontFamily="'Cormorant', serif"
                    fontWeight="700"
                    color="gray.800"
                    lineHeight="1.3"
                  >
                    We believe in quality that lasts, designs that speak
                  </Heading>
                  
                  <VStack spacing={4} align="start">
                    <Text
                      fontSize={{ base: "md", lg: "lg" }}
                      color="gray.600"
                      fontFamily="'Zain', sans-serif"
                      lineHeight="1.7"
                      fontWeight="500"
                    >
                      From design to delivery, every detail is handled with care, passion, and precision.
                    </Text>
                    <Text
                      fontSize={{ base: "md", lg: "lg" }}
                      color="gray.600"
                      fontFamily="'Zain', sans-serif"
                      lineHeight="1.7"
                      fontWeight="500"
                    >
                      This isn't just jewelry. This is a statement. This is AGNIR.
                    </Text>
                  </VStack>

                  <Box
                    p={6}
                    bg="linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)"
                    borderRadius="xl"
                    border="1px solid"
                    borderColor="gray.200"
                  >
                    <Text
                      fontSize={{ base: "xl", lg: "2xl" }}
                      fontFamily="'Cormorant', serif"
                      fontWeight="700"
                      color="gray.800"
                      fontStyle="italic"
                      textAlign="center"
                    >
                      "For the ones who wear presence."
                    </Text>
                  </Box>
                </VStack>
              </GridItem>

              <GridItem>
                <Box
                  bg="linear-gradient(135deg, #f1f3f4 0%, #e8eaed 100%)"
                  borderRadius="2xl"
                  p={8}
                  textAlign="center"
                  border="1px solid"
                  borderColor="gray.200"
                >
                  <Icon 
                    as={FaGem} 
                    boxSize={16} 
                    color="gray.500" 
                    mb={6}
                  />
                  <Heading
                    as="h3"
                    size="xl"
                    fontFamily="'Cormorant', serif"
                    color="gray.800"
                    mb={4}
                    fontWeight="700"
                  >
                    Premium Craftsmanship
                  </Heading>
                  <Text
                    fontSize="md"
                    color="gray.600"
                    fontFamily="'Zain', sans-serif"
                    lineHeight="1.6"
                    fontWeight="500"
                  >
                    Every piece undergoes meticulous quality checks and is crafted with precision to ensure it meets our high standards.
                  </Text>
                </Box>
              </GridItem>
            </Grid>
          </Box>
        </MotionBox>

        {/* Core Values Section */}
        <MotionBox
          mb={{ base: 12, md: 16, lg: 20 }}
          px={{ base: 4, md: 0 }}
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <VStack spacing={{ base: 8, lg: 12 }}>
            <Box textAlign="center">
              <Heading
                as="h2"
                size={{ base: "2xl", lg: "3xl" }}
                fontFamily="'Cormorant', serif"
                fontWeight="700"
                color="gray.800"
                mb={4}
              >
                Our Core Values
              </Heading>
              <Text
                fontSize={{ base: "md", lg: "lg" }}
                color="gray.600"
                fontFamily="'Zain', sans-serif"
                maxW="600px"
                mx="auto"
                lineHeight="1.6"
                fontWeight="500"
              >
                The principles that drive everything we do at AGNIR — from design to customer experience.
              </Text>
            </Box>

            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={{ base: 6, lg: 8 }} w="full">
              {coreValues.map((value, index) => (
                <MotionBox
                  key={value.title}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.1 * index }}
                >
                  <VStack
                    spacing={4}
                    p={{ base: 6, lg: 8 }}
                    bg="white"
                    borderRadius="xl"
                    textAlign="center"
                    border="1px solid"
                    borderColor="gray.200"
                    boxShadow="0 4px 20px rgba(0, 0, 0, 0.08)"
                    _hover={{
                      transform: "translateY(-5px)",
                      boxShadow: "0 8px 30px rgba(0, 0, 0, 0.15)"
                    }}
                    transition="all 0.3s ease"
                    minH={{ base: "240px", md: "260px", lg: "280px" }}
                    maxH={{ base: "240px", md: "260px", lg: "280px" }}
                    justify="space-between"
                    position="relative"
                  >
                    <Box
                      p={4}
                      borderRadius="full"
                      bg="linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)"
                      border="1px solid"
                      borderColor="gray.200"
                    >
                      <Icon as={value.icon} boxSize={7} color="gray.600" />
                    </Box>
                    
                    <Heading
                      as="h3"
                      size="lg"
                      fontFamily="'Cormorant', serif"
                      fontWeight="700"
                      color="gray.800"
                    >
                      {value.title}
                    </Heading>
                    
                    <Text
                      fontSize={{ base: "xs", md: "sm" }}
                      color="gray.600"
                      fontFamily="'Zain', sans-serif"
                      lineHeight="1.6"
                      flex="1"
                      display="flex"
                      alignItems="center"
                      textAlign="center"
                      fontWeight="500"
                    >
                      {value.description}
                    </Text>
                  </VStack>
                </MotionBox>
              ))}
            </SimpleGrid>
          </VStack>
        </MotionBox>

        {/* Call to Action Section */}
        <MotionBox
          px={{ base: 4, md: 0 }}
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
        >
          <Box
            bg="linear-gradient(135deg, #1a202c 0%, #2d3748 100%)"
            borderRadius="3xl"
            p={{ base: 8, lg: 16 }}
            color="white"
            textAlign="center"
            boxShadow="0 20px 60px rgba(0, 0, 0, 0.2)"
            position="relative"
            overflow="hidden"
          >
            {/* Background Pattern */}
            <Box
              position="absolute"
              top="0"
              left="0"
              right="0"
              bottom="0"
              backgroundImage="radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255,255,255,0.1) 0%, transparent 50%)"
            />
            
            <VStack spacing={{ base: 8, lg: 12 }} position="relative" zIndex={1}>
              <VStack spacing={4}>
                <Heading
                  as="h2"
                  fontSize={{ base: "4xl", lg: "5xl" }}
                  fontFamily="'Cormorant', serif"
                  fontWeight="700"
                  textAlign="center"
                  lineHeight="1.2"
                >
                  Ready to Make Your Statement?
                </Heading>
                <Text
                  fontSize={{ base: "lg", lg: "xl" }}
                  fontFamily="'Zain', sans-serif"
                  opacity="0.9"
                  maxW="600px"
                  lineHeight="1.6"
                  fontWeight="500"
                >
                  Discover our collection, get your questions answered, or connect with our team. 
                  Your journey to wearing presence starts here.
                </Text>
              </VStack>

              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={{ base: 6, lg: 8 }} w="full" maxW="930px">
                {ctaOptions.map((option, index) => (
                  <MotionBox
                    key={option.title}
                    as={Link}
                    href={option.href}
                    textDecoration="none"
                    _hover={{ textDecoration: 'none' }}
                    whileHover={{ y: -5, scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <VStack
                      spacing={4}
                      p={{ base: 6, lg: 8 }}
                      bg={option.bg}
                      color={option.color}
                      borderRadius="xl"
                      textAlign="center"
                      border="1px solid"
                      borderColor={option.color === 'white' ? 'rgba(255,255,255,0.2)' : 'gray.200'}
                      minH="180px"
                      justify="center"
                      transition="all 0.3s ease"
                      _hover={{
                        transform: "translateY(-5px)",
                        boxShadow: option.color === 'white' 
                          ? "0 10px 30px rgba(255,255,255,0.1)" 
                          : "0 10px 30px rgba(0,0,0,0.15)"
                      }}
                    >
                      <Box
                        p={3}
                        borderRadius="full"
                        bg={option.color === 'white' ? 'rgba(255,255,255,0.1)' : 'gray.100'}
                      >
                        <Icon 
                          as={option.icon} 
                          boxSize={6} 
                          color={option.color === 'white' ? 'white' : 'gray.600'} 
                        />
                      </Box>
                      
                      <VStack spacing={2}>
                        <Heading
                          as="h3"
                          size="lg"
                          fontFamily="'Cormorant', serif"
                          fontWeight="700"
                        >
                          {option.title}
                        </Heading>
                        <Text
                          fontSize="sm"
                          fontFamily="'Zain', sans-serif"
                          opacity="0.8"
                          lineHeight="1.5"
                          fontWeight="500"
                        >
                          {option.description}
                        </Text>
                      </VStack>

                      <HStack spacing={2} mt={2}>
                        <Text fontSize="sm" fontWeight="600" fontFamily="'Zain', sans-serif">
                          Learn More
                        </Text>
                        <Icon as={FaArrowRight} boxSize={3} />
                      </HStack>
                    </VStack>
                  </MotionBox>
                ))}
              </SimpleGrid>
            </VStack>
          </Box>
        </MotionBox>
      </Container>
    </Box>
    </>
  );
};

export default AboutUsPage;